import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSmsConversation, sendSms, newClientRequestId, getHeldSms, cancelHeldSms,
} from '@lib/care-api';
import { useSmsInbound } from '@lib/sms-inbound';
import { langLabel, isNonEnglish, fmtPhone } from '@lib/care-model';
import { composeDestination, choiceLabel } from '@lib/send-destination';
import type {
  ClaireInteractionRecord, HeldSmsRecord, SmsConversationResp, SmsMessageRecord, SmsSendResp,
} from '@lib/care-model';
import { heldShown, heldStatusLine, heldUntilLabel } from '@lib/sms-hold';

/**
 * The member-centric UNIFIED THREAD - every SMS (both directions) plus every Claire interaction
 * (voice calls + Claire-line texts) in one timeline, joined on the member's phones (fleet contract
 * 2026-07-11). SMS rows are the LIVE MIRROR (no-DB / Option A, William 2026-07-10): pulled from
 * cdx-sms `/sms/thread` on open, on a light background poll, and on the operator's Refresh. The
 * console keeps NO copy; ClariDynamics' database is the system of record. Claire rows come from our
 * own event store, riding the same response.
 *
 * WHY IT LOOKS THE WAY IT DOES. If Jennie is texting Sam and William is also texting Sam about
 * something else, both see the whole dialog with Sam and each message says who sent it. There is no
 * claim lock on a conversation - attribution replaces exclusivity.
 *
 * TRANSLATION IS ONE TOGGLE FOR BOTH CHANNELS. SMS rows re-fetch with ?translate=1 (per-message
 * `translatedText`); Claire rows already carry their call-level English translation and the toggle
 * only gates DISPLAY. Language chips key on ISO 639-1 for both (Claire's display enum maps via the
 * console-owned 5-row map, server-side).
 *
 * A SEND HAS FOUR OUTCOMES, ONLY ONE OF WHICH REACHES THE MEMBER. The platform answers HTTP 200 for
 * three of them (sent / suppressed / quiet_hours) and only `sent` was delivered; a transport failure
 * is a fourth, `unknown`, where the message MAY have gone. None of the last three is drawn as a
 * delivered bubble - they surface as a transient notice, and a delivered send reappears from the
 * mirror on the re-fetch that follows it.
 */
type Props = {
  memberId?: string;
  phone?: string;
  /** Row 626: the roster the ENTRY belongs to (entryRosterCampaign), so the member is found on it
   *  and not only on 26 FOBT. Absent = the server's own order (reference, then the current rosters). */
  campaign?: string;
  /** The event the operator had open when they sent. Provenance only; never a grouping key. */
  contextEventId?: string;
  /** Signed-in operator's UPN, so their own messages can be distinguished from a colleague's. */
  me?: string;
  /** Lifts the member-level do-not-contact signal to the drawer so it can warn near the phone
   *  actions. Called after each successful load with the current signals. */
  onLoad?: (info: { doNotSolicit: string; suppressed: boolean; suppressionKnown: boolean; resubscribedTs: string }) => void;
  /** A counter the parent bumps to say "the operator asked to text": the compose box scrolls into
   *  view and takes focus. A counter rather than a boolean, so a second press re-focuses. */
  focusSignal?: number;
  /**
   * R. 82 / row 401: the thread of an ARCHIVED entry. The history still shows (it is the member's
   * thread, not the entry's), but the compose box is not offered: nothing is sent from a record
   * that is finished with.
   */
  readOnly?: boolean;
};

const POLL_MS = 20_000;

// Cold-start warm-up (William 2026-08-20). The platform's Container App scales to zero when idle;
// the FIRST send after a quiet stretch hits 503 before the app exists. That 503 is the one failure
// whose clientRequestId is unspent AND safe to replay, so instead of dumping an error on the
// operator we promise 30 seconds on screen, keep replaying the same request, and stop the moment
// the platform answers with anything that is not a wake. The budget lives HERE, not in the API's
// retry envelope, because a single function invocation that waits out a container wake would flirt
// with the SWA 45s response cap (the exact cap that killed the Archive page on 2026-08-03).
const WARM_TOTAL_S = 30;
const WARM_RETRY_GAP_MS = 2_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** True when a send response is the platform-waking-from-idle 503 (nothing sent, id replay-safe). */
function isWakingUp(res: { status: number; data?: unknown }): boolean {
  return res.status === 503 && (res.data as { status?: unknown } | undefined)?.status === 'unavailable';
}

function fmtTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtClock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** An outbound message the carrier rejected. The UI must never draw these as delivered. */
function undelivered(m: SmsMessageRecord): boolean {
  return m.direction === 'out' && m.deliveryStatus === 'REJECTED';
}

/** The delivery state of a historical outbound row, from the mirror. */
function StatusNote({ m }: { m: SmsMessageRecord }): JSX.Element | null {
  if (m.direction !== 'out') return null;
  switch (m.deliveryStatus) {
    case 'DELIVERED':
      return <span className="sms-status sms-status-sent">Delivered</span>;
    case 'SENT':
      return <span className="sms-status sms-status-muted">Sent</span>;
    case 'QUEUED':
      return <span className="sms-status sms-status-muted">Queued</span>;
    case 'REJECTED':
      return <span className="sms-status sms-status-blocked">Not delivered</span>;
    default:
      return null;
  }
}

/** One row of the unified timeline: an SMS message, or a whole Claire interaction. */
type ThreadItem =
  | { kind: 'sms'; ts: string; key: string; m: SmsMessageRecord }
  | { kind: 'claire'; ts: string; key: string; c: ClaireInteractionRecord }
  | { kind: 'held'; ts: string; key: string; h: HeldSmsRecord };

/** Merge SMS messages + Claire interactions (+ held texts, the after-hours hold) into one newest-first timeline. */
function mergeThread(messages: SmsMessageRecord[], claire: ClaireInteractionRecord[], held: HeldSmsRecord[] = []): ThreadItem[] {
  const items: ThreadItem[] = [
    ...messages.map((m): ThreadItem => ({ kind: 'sms', ts: m.ts, key: `s:${m.messageId}`, m })),
    ...claire.map((c): ThreadItem => ({ kind: 'claire', ts: c.ts, key: `c:${c.eventId}`, c })),
    ...held.map((h): ThreadItem => ({ kind: 'held', ts: h.heldAt, key: `h:${h.clientRequestId}`, h })),
  ];
  // Newest first (William 2026-07-10). EPOCH comparison, not lexical: the two sources may emit
  // different ISO offset styles ('...Z' vs '...-07:00'), and lexical order is wrong across them.
  // An unparseable ts sorts oldest (bottom); the key tiebreak keeps order stable across polls.
  const t = (x: ThreadItem): number => Date.parse(x.ts) || 0;
  return items.sort((a, b) => (t(b) - t(a)) || a.key.localeCompare(b.key));
}

/** The persisted Translate preference (per browser/operator). Storage failures read as "off". */
const TRANSLATE_LS_KEY = 'membercare.sms.translate';
function loadTranslatePref(): boolean {
  try {
    return localStorage.getItem(TRANSLATE_LS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * One Claire interaction in the thread: a voice call (or Claire-line text) card. Distinct dress from
 * an SMS bubble so a call never reads as a text. The language chip prefers Claire's own display name
 * (it can be a language outside the ISO label map, e.g. "Tagalog"); the English block renders only
 * under the Translate toggle, mirroring the SMS rows.
 */
function ClaireRow({ c, translate }: { c: ClaireInteractionRecord; translate: boolean }): JSX.Element {
  const body = c.summary || c.reason || c.subject || 'Claire interaction';
  const showReason = !!c.reason && c.reason !== body;
  return (
    <div className="sms-msg sms-in sms-claire">
      <div className="sms-meta">
        <span className="sms-who">{c.channel === 'voice' ? '☎ Voice call - Claire' : 'Text - Claire line'}</span>
        <span className="sms-time">{fmtTs(c.ts)}</span>
        {c.hasAudio && <span title="recording available">♪</span>}
        {c.claireResolved && <span className="pill pill-success">Claire Auto Resolved</span>}
        {isNonEnglish(c.lang) && (
          <span className="pill pill-accent sms-lang" title={`Call language: ${c.language || langLabel(c.lang)}`}>
            {c.language || langLabel(c.lang)}
          </span>
        )}
      </div>
      <div className="sms-body">{body}</div>
      {showReason && <div className="sms-claire-reason">Reason: {c.reason}</div>}
      {translate && (c.translatedReason || c.translatedTranscript) && (
        <div className="sms-translation">
          <span className="sms-translation-label">English</span>
          {c.translatedReason && <span className="sms-translation-text">{c.translatedReason}</span>}
          {c.translatedTranscript && (
            <details className="sms-claire-transcript">
              <summary>English transcript</summary>
              <div>{c.translatedTranscript}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One held text (the after-hours hold, William 16 Oct 2026): an outbound bubble in its own dress, its state line,
 * and a Cancel button while it is held. It NEVER says Sent unless the release really sent it (state 'sent').
 */
function HeldRow({ h, onCancel, busy }: { h: HeldSmsRecord; onCancel: (cid: string) => void; busy: boolean }): JSX.Element {
  return (
    <div className={`sms-msg sms-out sms-held sms-held-${h.state}`} data-testid="sms-held">
      <div className="sms-meta">
        <span className="sms-who">{h.operatorName || h.operator || 'Console'}</span>
        <span className="sms-time">{fmtTs(h.heldAt)}</span>
        <span className="pill pill-warning">Held</span>
      </div>
      <div className="sms-body">{h.text}</div>
      <div className="sms-held-line" data-testid="sms-held-line">{heldStatusLine(h)}</div>
      {h.state === 'held' && (
        <button type="button" className="btn btn-subtle sms-held-cancel" onClick={() => onCancel(h.clientRequestId)} disabled={busy}>
          Cancel
        </button>
      )}
    </div>
  );
}

export default function SmsConversation({ memberId, phone, campaign, contextEventId, me, onLoad, focusSignal, readOnly = false }: Props): JSX.Element {
  const [data, setData] = useState<SmsConversationResp | null>(null);
  // Latest onLoad kept in a ref so `load`'s identity does not churn on the parent re-rendering (which
  // would tear down and rebuild the poll interval every render). Fires after each successful load.
  const onLoadRef = useRef(onLoad);
  useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);
  const [err, setErr] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  // The after-hours hold: this member's held texts, read only when the conversation says smsHold.
  const [held, setHeld] = useState<HeldSmsRecord[]>([]);
  const [heldErr, setHeldErr] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'warn' | 'error'; msg: string } | null>(null);
  // Seconds left on the warm-up countdown, or null when no warm-up is running. Rendered as its own
  // banner (not through `notice`) so a tick cannot race a real outcome message.
  const [warmLeft, setWarmLeft] = useState<number | null>(null);
  // Bumped when the drawer moves to another member (or unmounts) so an in-flight warm-up loop stops
  // quietly instead of replaying the old member's message from under the new view.
  const warmCancelRef = useRef(0);
  useEffect(() => () => { warmCancelRef.current++; }, [memberId, phone]);
  // Translate toggle (persisted per operator; off by default): when on, the load asks the mirror for
  // English `translatedText` on inbound non-English rows, and the Claire rows show their call-level
  // English. It must ride EVERY load - including the background poll - or a poll would overwrite the
  // translated rows with untranslated ones.
  const [translate, setTranslate] = useState(loadTranslatePref);
  useEffect(() => {
    try {
      localStorage.setItem(TRANSLATE_LS_KEY, translate ? '1' : '0');
    } catch { /* private mode etc.: the toggle still works for this session */ }
  }, [translate]);

  // The idempotency key survives a transport-unknown attempt on purpose: retrying the SAME id makes
  // the platform replay its first result rather than send a second text. A DEFINITE failure spends
  // the id, and we mint a fresh one so the retry actually sends.
  const cidRef = useRef<string>(newClientRequestId());
  // Row 847: the number the held id was last sent to. A held id (kept after a 502, a 503 or a 429) is
  // a safe replay only of the SAME message to the SAME number, so a send to another number mints a
  // fresh id first, and a send to the same number keeps it. null = the id has not been sent yet.
  const cidForRef = useRef<string | null>(null);
  // ROW 847 (his word, option A, 4 Oct 2026): the number the operator picked in the Text box. A pick
  // belongs to THIS case only (keyed by the member, the case's number and the case), so another case
  // opens on today's default with no effect to wait for. '' = the default.
  const pickFor = `${memberId ?? ''}|${phone ?? ''}|${contextEventId ?? ''}`;
  const [pick, setPick] = useState<{ for: string; phone: string }>({ for: '', phone: '' });
  const picked = pick.for === pickFor ? pick.phone : '';
  // The compose box, so the drawer's Text button can hand the operator straight to it. The first
  // render carries focusSignal 0 and must NOT steal focus - only a bump does.
  const composeRef = useRef<HTMLTextAreaElement>(null);
  const lastFocusSignal = useRef<number>(focusSignal ?? 0);
  const wantFocus = useRef(false);
  useEffect(() => {
    const sig = focusSignal ?? 0;
    if (sig !== lastFocusSignal.current) {
      lastFocusSignal.current = sig;
      wantFocus.current = true;
      // Row 847: the drawer's Text buttons stand beside the DEFAULT number (the case's own, else the
      // record's first), so a press puts the picker back on it: the button never promises a number
      // the box will not use.
      setPick({ for: '', phone: '' });
    }
    if (!wantFocus.current) return;
    const el = composeRef.current;
    // The box is not on screen yet: the first load is still in flight (this effect re-runs when it
    // lands), or the rail is unconfigured and there will never be a box. The want-flag simply
    // survives until one of those resolves.
    if (!el) return;
    wantFocus.current = false;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus();
  }, [focusSignal, data]);
  // Monotonic load id: only the MOST RECENT load may commit its response. The response body now depends
  // on the Translate flag, so a slow translate=1 fetch resolving AFTER a newer translate=0 fetch (or the
  // reverse) must not overwrite the current view - that would show translations while the box reads off,
  // or strip them while it reads on. Latest-request-wins; the poll self-heals either way.
  const loadSeqRef = useRef(0);
  const { markSeen } = useSmsInbound();

  const load = useCallback(async (opts?: { manual?: boolean }) => {
    if (!memberId && !phone) return;
    if (opts?.manual) setRefreshing(true);
    const seq = ++loadSeqRef.current;
    try {
      const args: { memberId?: string; phone?: string; campaign?: string; translate?: boolean } = {};
      if (memberId) args.memberId = memberId;
      if (phone) args.phone = phone;
      if (memberId && campaign) args.campaign = campaign;
      if (translate) args.translate = true;
      const resp = await getSmsConversation(args);
      if (seq !== loadSeqRef.current) return; // a newer load started; drop this stale response
      // THE AFTER-HOURS HOLD: asked only when the server says the switch is on (or test), so with it off this
      // load is today's, one request and nothing more.
      let heldNow: HeldSmsRecord[] = [];
      let heldProblem = '';
      if (resp.smsHold) {
        try {
          const hr = await getHeldSms({ ...(memberId ? { memberId } : {}), phones: [...resp.phones, phone ?? ''] });
          heldNow = hr.items;
        } catch {
          heldProblem = 'The held texts could not be read right now. Press Refresh.';
        }
        if (seq !== loadSeqRef.current) return;
      }
      setHeld(heldNow);
      setHeldErr(heldProblem);
      setData(resp);
      onLoadRef.current?.({
        doNotSolicit: resp.doNotSolicit ?? '', suppressed: resp.suppressed, suppressionKnown: resp.suppressionKnown,
        // The platform's own re-subscribe stamp, off the suppression row we already fetch. The feed
        // is activeOnly, and the platform does not clear resubscribedAt when it re-suppresses, so a
        // row that still carries one is a member who re-subscribed and then opted out AGAIN. The
        // banner says exactly that and nothing softer: this is a CURRENT opt-out.
        resubscribedTs: (resp.suppressionRows ?? []).find((r) => r.resubscribedTs)?.resubscribedTs ?? '',
      });
      // Opening (or refreshing) a member's dialog clears its unread-text badge - the operator is
      // looking at it. Covers the case of a reply landing while the drawer is open.
      markSeen([...resp.phones, phone]);
      setErr('');
      setLoadedAt(new Date());
    } catch (e) {
      if (seq !== loadSeqRef.current) return; // superseded; do not clobber the newer load's state
      setErr((e as Error).message || 'could not load the conversation');
    } finally {
      if (opts?.manual) setRefreshing(false);
    }
  }, [memberId, phone, campaign, translate, markSeen]);

  useEffect(() => { void load(); }, [load]);

  // A colleague may be texting this member right now, and new replies land continuously. Poll so the
  // transcript stays live without the operator clicking Refresh.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === 'visible') void load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const onSend = async (): Promise<void> => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setNotice(null);

    // Row 847: the send names the number on the screen (the picked one, else today's default). Before
    // the picker it named the case's number, or none; composeDestination keeps exactly that whenever
    // the read carries no labelled record numbers.
    const dest = composeDestination({ casePhone: phone, recordPhones: data?.recordPhones, phones: data?.phones ?? [], picked });
    if (cidForRef.current !== null && cidForRef.current !== dest.send) cidRef.current = newClientRequestId();
    cidForRef.current = dest.send;

    const args: { text: string; clientRequestId: string; memberId?: string; phone?: string; campaign?: string; contextEventId?: string } = {
      text: body, clientRequestId: cidRef.current,
    };
    if (memberId) args.memberId = memberId;
    if (dest.send) args.phone = dest.send;
    if (memberId && campaign) args.campaign = campaign;
    if (contextEventId) args.contextEventId = contextEventId;

    const token = warmCancelRef.current;
    let res = await sendSms(args);

    // The platform is waking from a cold start: nothing was sent and this exact request is safe to
    // replay. Show the countdown and keep trying until it answers or the promise runs out. The
    // countdown ends EARLY on the first non-wake answer (usually "sent").
    if (isWakingUp(res)) {
      const started = Date.now();
      setWarmLeft(WARM_TOTAL_S);
      const tick = setInterval(() => {
        setWarmLeft(Math.max(0, WARM_TOTAL_S - Math.round((Date.now() - started) / 1000)));
      }, 500);
      try {
        while (Date.now() - started < WARM_TOTAL_S * 1000) {
          await sleep(WARM_RETRY_GAP_MS);
          if (warmCancelRef.current !== token) break; // drawer moved on; stop replaying
          res = await sendSms(args);
          if (warmCancelRef.current !== token || !isWakingUp(res)) break;
        }
      } finally {
        clearInterval(tick);
        setWarmLeft(null);
      }
    }
    if (warmCancelRef.current !== token) {
      // The operator opened another member mid-warm-up. Whatever this loop learned belongs to the
      // OLD member's view, which is gone; just release the compose state and say nothing.
      setSending(false);
      return;
    }
    setSending(false);

    if (res.ok && res.data) {
      const r = res.data as SmsSendResp;
      cidRef.current = newClientRequestId(); // settled: the next message is a new message
      cidForRef.current = null;
      setText('');
      if (r.delivered) setNotice({ kind: 'ok', msg: 'Sent.' });
      else if (r.status === 'held') setNotice({ kind: 'warn', msg: `NOT SENT YET. Outside texting hours. ${heldUntilLabel(r.heldUntil, r.memberZone)}: it sends by itself then.` });
      else if (r.status === 'suppressed') setNotice({ kind: 'warn', msg: `NOT SENT. This member is opted out${r.reason ? ` (${r.reason})` : ''}. Nothing was delivered.` });
      else if (r.status === 'quiet_hours') setNotice({ kind: 'warn', msg: 'NOT SENT. Outside texting hours. Nothing was delivered.' });
      await load();
      return;
    }

    // The server names the failure in the body ({ error }); prefer its words over a bare code.
    const serverMsg = typeof (res.data as { error?: unknown })?.error === 'string'
      ? String((res.data as { error: string }).error)
      : '';

    // 502 = a transport-unknown: the platform never answered. KEEP the clientRequestId - pressing Send
    // again replays the original request instead of sending a second text.
    if (res.status === 502) {
      setNotice({
        kind: 'error',
        msg: 'The SMS platform did not answer. Your message may or may not have been delivered. Press Send again to retry safely - it cannot double-text the member.',
      });
      await load();
      return;
    }
    // 503 = nothing was sent and the id is unspent (rail unconfigured, the stop list could not be read, or
    // the platform waking from idle; the server's message says which). Keep the id and retry. A text held
    // because the stop list could not be read shows the server's one sentence (SENDWORDS, T-1010-99).
    if (res.status === 503) {
      setNotice({
        kind: 'error',
        msg: isWakingUp(res)
          // Only reachable after the warm-up loop above spent its full budget replaying this send.
          ? `The SMS server is still warming up after ${WARM_TOTAL_S} seconds. Nothing was sent - press Send to keep trying (it cannot double-text the member).`
          : (serverMsg || 'The SMS service is unavailable right now, so nothing was sent. Try again shortly.'),
      });
      return;
    }
    // 429 = a console volume guardrail held it back (per-number day, per-operator hour, whole-console
    // day, or the duplicate-text window). Nothing reached the platform, so the id is unspent and is
    // KEPT. This is a brake rather than a failure, so it reads as a warning in the platform's own
    // vocabulary: the server's sentence already opens with NOT SENT and says which rule fired.
    if (res.status === 429) {
      setNotice({ kind: 'warn', msg: serverMsg || 'NOT SENT. A console sending limit held this message back.' });
      return;
    }
    // Any other error is a DEFINITE non-send: mint a fresh id so the retry actually sends.
    cidRef.current = newClientRequestId();
    cidForRef.current = null;
    setNotice({ kind: 'error', msg: serverMsg ? `Not sent - ${serverMsg}` : `Not sent (${res.status}).` });
  };

  const onCancelHeld = async (cid: string): Promise<void> => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const r = await cancelHeldSms(cid);
      const words = typeof (r.data as { error?: unknown } | undefined)?.error === 'string' ? String((r.data as { error: string }).error) : '';
      setNotice(r.ok ? { kind: 'ok', msg: 'Cancelled. The held text will not be sent.' } : { kind: 'error', msg: words || `The cancel did not go through (${r.status}).` });
      await load();
    } finally {
      setCancelling(false);
    }
  };

  if (err) {
    return (
      <div className="sms-panel">
        <header className="sms-head">
          <h4>Messages &amp; calls</h4>
          <button type="button" className="btn btn-subtle sms-refresh" onClick={() => void load({ manual: true })} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </header>
        <p className="error">{err}</p>
      </div>
    );
  }
  if (!data) return <div className="sms-panel"><p className="muted">Loading conversation...</p></div>;

  const claireRows = data.claireInteractions ?? [];

  if (!data.smsConfigured && claireRows.length === 0) {
    return (
      <div className="sms-panel">
        <p className="muted">The SMS rail is not configured in this environment.</p>
        {data.claireComplete === false && (
          <p className="muted">Call history is unavailable right now - this is not the same as "no calls".</p>
        )}
      </div>
    );
  }

  const smsOn = data.smsConfigured;
  const blocked = !data.canSend;
  const blockReason = data.suppressed
    ? `This member is opted out of SMS${data.suppressionRows[0]?.reason ? ` (${data.suppressionRows[0]!.reason})` : ''}. Texting is disabled.`
    : !data.suppressionKnown
      // SENDWORDS (T-1010-99): the panel's read failed, which may be a platform that answered with no list in
      // it, so this says the list could not be read and never that the platform was unreachable.
      ? 'The stop list could not be read, so sending is disabled. This is not the same as "safe to text".'
      : '';

  const timeline = mergeThread(data.messages, claireRows, data.smsHold ? held.filter((h) => heldShown(h)) : []);
  // The number a Send would actually reach. Row 847: the operator may pick among the case's own number
  // and the record's two (each once, today's order); the default is today's (the case's number, else
  // the record's phone1, then phone2), and the send names exactly the number shown here.
  const dest = composeDestination({ casePhone: phone, recordPhones: data.recordPhones, phones: data.phones, picked });

  return (
    <div className="sms-panel">
      <header className="sms-head">
        <div className="sms-head-title">
          <h4>Messages &amp; calls{data.memberName ? ` with ${data.memberName}` : ''}</h4>
          <span className="muted">{data.phones.map((p) => fmtPhone(p)).join(', ')}</span>
        </div>
        <div className="sms-head-actions">
          {loadedAt && <span className="muted sms-updated">Updated {fmtClock(loadedAt)}</span>}
          <label className="sms-translate" title="Show an English translation of non-English texts and calls">
            <input type="checkbox" checked={translate} onChange={(e) => setTranslate(e.target.checked)} />
            Translate
          </label>
          <button type="button" className="btn btn-subtle sms-refresh" onClick={() => void load({ manual: true })} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </header>

      {smsOn && blocked && <p className="sms-blocked">{blockReason}</p>}
      {!smsOn && <p className="muted">The SMS rail is not configured - showing the call history only.</p>}
      {data.claireComplete === false && (
        <p className="muted">Call history is unavailable right now - this is not the same as "no calls".</p>
      )}
      {warmLeft !== null && (
        <p className="sms-notice sms-notice-warn" role="status">
          SMS server is warming up - your message will send automatically. Up to {warmLeft}s remaining...
        </p>
      )}
      {warmLeft === null && notice && <p className={`sms-notice sms-notice-${notice.kind}`}>{notice.msg}</p>}
      {data.smsHold && heldErr && <p className="sms-notice sms-notice-error">{heldErr}</p>}

      {smsOn && readOnly && <p className="muted" data-testid="sms-compose-readonly">Archived entry: the conversation is shown for the record; nothing can be sent from here.</p>}
      {smsOn && !readOnly && (
        <div className="sms-compose">
          <textarea
            ref={composeRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={blocked ? 'Texting is disabled for this member' : 'Type a message to the member'}
            disabled={blocked || sending}
            rows={3}
            maxLength={1600}
          />
          {/* ROW 847 (his word, option A, 4 Oct 2026): THE NUMBER PICKER. Offered only when the case
              and the record hold two or more different numbers; with one, the box is exactly as it
              was. The server sends to a picked number only when it is one of these three. */}
          {dest.choices.length > 1 && (
            <label className="sms-dest">
              <span>Text to</span>
              <select
                data-testid="sms-dest-picker"
                aria-label="Number to text"
                value={dest.send}
                onChange={(e) => setPick({ for: pickFor, phone: e.target.value })}
                disabled={blocked || sending}
              >
                {dest.choices.map((c) => (
                  <option key={c.phone} value={c.phone}>{`${fmtPhone(c.phone)}, ${choiceLabel(c)}`}</option>
                ))}
              </select>
            </label>
          )}
          <div className="sms-compose-actions">
            {/* WHICH NUMBER THIS GOES TO. The send names this exact number (row 847: the picked one,
                else today's default), so for a member with two numbers the destination is not a
                guess. Saying it out loud is the difference between texting the member and texting a
                line they no longer use. */}
            {dest.shown && <span className="muted">To {fmtPhone(dest.shown)}</span>}
            <span className="muted">{text.trim().length}/1600</span>
            <button type="button" className="btn btn-primary" onClick={() => void onSend()} disabled={blocked || sending || !text.trim()}>
              {sending ? (warmLeft !== null ? 'Warming up...' : 'Sending...') : 'Send'}
            </button>
          </div>
        </div>
      )}

      <div className="sms-thread" role="log" aria-label="Messages and calls">
        {timeline.length === 0 && <p className="muted">No messages or calls yet.</p>}
        {/* Newest first (William 2026-07-10): the most recent item renders on top. mergeThread
            sorts a fresh array, so the source lists are never mutated. */}
        {timeline.map((item) => item.kind === 'claire' ? (
          <ClaireRow key={item.key} c={item.c} translate={translate} />
        ) : item.kind === 'held' ? (
          <HeldRow key={item.key} h={item.h} onCancel={(cid) => void onCancelHeld(cid)} busy={cancelling} />
        ) : (
          <div
            key={item.key}
            className={[
              'sms-msg',
              item.m.direction === 'in' ? 'sms-in' : 'sms-out',
              undelivered(item.m) ? 'sms-undelivered' : '',
              item.m.automated ? 'sms-automated' : '',
              item.m.direction === 'out' && me && item.m.operator === me ? 'sms-mine' : '',
            ].filter(Boolean).join(' ')}
          >
            <div className="sms-meta">
              <span className="sms-who">
                {item.m.direction === 'in'
                  ? (data.memberName || 'Member')
                  /* The agent attribution. This is the whole point of the screen. */
                  : (item.m.operatorName || 'Console')}
              </span>
              <span className="sms-time">{fmtTs(item.m.ts)}</span>
              {item.m.keyword && <span className="pill pill-warning">{item.m.keyword}</span>}
              {isNonEnglish(item.m.detectedLang) && (
                <span className="pill pill-accent sms-lang" title={`Detected language: ${langLabel(item.m.detectedLang)}`}>
                  {langLabel(item.m.detectedLang)}
                </span>
              )}
            </div>
            <div className="sms-body">{item.m.body}</div>
            {item.m.translatedText && (
              <div className="sms-translation">
                <span className="sms-translation-label">English</span>
                <span className="sms-translation-text">{item.m.translatedText}</span>
              </div>
            )}
            <StatusNote m={item.m} />
          </div>
        ))}
      </div>
    </div>
  );
}
